import { memo, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ButtonWithIcon } from 'common-components'
import { DividerLine } from 'common-components/DividerLine'
import { ScrollableElementProvider } from 'containers/SubSidebar/AIVideoContent/context'
import { T } from 'consts/translationKeys'
import { FETCH_LIMIT } from 'containers/SubSidebar/AiLibContent/consts'
import { aiImageThunks } from 'store/features/aiImage/thunks'
import { TAppDispatch } from 'store/types'
import { aiLibImageSelectors } from 'store/features/aiLibImage/selectors'
import { aiLibImageThunks } from 'store/features/aiLibImage/thunks'
import { aiLibThunks as aiLibVideoThunks } from 'store/features/aiLibVideo/thunks'
import { translate } from 'utils/translations'
import * as Styled from './styled'
import FiltersSection from './FiltersSection'
import { useFetchAiLibHistoryOnScroll } from './hooks/useFetchAiLibHistoryOnScroll'
import Filter from 'assets/svgr-icons/filter.svg'
import { IAIImageProject } from 'store/types/aiImage'
import ImageItem from './ImageItem'
import { usePreviewNavigation } from '../hooks/usePreviewNavigation'
import { NotFound } from './NotFound'

const ImagesTab = () => {
  const isFiltersOpen = useSelector(aiLibImageSelectors.isFiltersOpen)
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(
    null
  )
  const dispatch = useDispatch<TAppDispatch>()
  const scrollableElementRef = useMemo(
    () => ({ current: scrollContainer }),
    [scrollContainer]
  )
  useFetchAiLibHistoryOnScroll(scrollContainer)

  const isInitialLoading = useSelector(aiLibImageSelectors.isInitialLoading)
  useEffect(() => {
    if (isInitialLoading && scrollContainer) {
      scrollContainer.scrollTop = 0
    }
  }, [isInitialLoading, scrollContainer])

  const isLoaded = useSelector(aiLibImageSelectors.isLoaded)
  const hasActiveFilters = useSelector(aiLibImageSelectors.hasActiveFilters)
  const activeFiltersCount = useSelector(aiLibImageSelectors.activeFiltersCount)
  const historyList = useSelector(aiLibImageSelectors.historyList)

  const {
    activePreviewIndex,
    handleOpenPreview,
    handleClosePreview,
    handleNextPreview,
    handlePrevPreview
  } = usePreviewNavigation(historyList.length)

  useEffect(() => {
    if (!isLoaded) {
      dispatch(aiLibImageThunks.getHistory({ offset: 0, limit: FETCH_LIMIT }))
    }
  }, [dispatch, isLoaded])

  useEffect(() => {
    dispatch(aiImageThunks.getModels())
    dispatch(aiImageThunks.getStyles())
  }, [dispatch])

  const handleClearFilters = () => {
    dispatch(aiLibImageThunks.resetFilters())
  }

  return (
    <ScrollableElementProvider scrollableElementRef={scrollableElementRef}>
      <Styled.Container>
        <Styled.Controls>
          <Styled.FilterButton $isOpen={isFiltersOpen}>
            <ButtonWithIcon
              className="filter-btn"
              icon={<Filter />}
              label={`${translate(T.FILTER_LABEL)}${activeFiltersCount > 0 ? ` (${activeFiltersCount})` : ''}`}
              onClick={() => {
                dispatch(aiLibImageThunks.toggleFiltersOpen())
                dispatch(aiLibVideoThunks.toggleFiltersOpen())
              }}
              iconSize="medium"
            />
          </Styled.FilterButton>
          {hasActiveFilters && (
            <Styled.ResetButton onClick={handleClearFilters}>
              {translate(T.RESET_LABEL)}
            </Styled.ResetButton>
          )}
        </Styled.Controls>
        <DividerLine />
        {isFiltersOpen ? <FiltersSection /> : null}
        <Styled.ItemsContainer ref={setScrollContainer}>
          {historyList.length === 0 && isLoaded ? (
            <NotFound />
          ) : (
            <>
              {historyList.map((item: IAIImageProject, index: number) => (
                <ImageItem
                  key={item.id}
                  item={item}
                  index={index}
                  isPreviewOpen={activePreviewIndex === index}
                  onOpenPreview={handleOpenPreview}
                  onClosePreview={handleClosePreview}
                  onNext={handleNextPreview}
                  onPrev={handlePrevPreview}
                  hasNext={index < historyList.length - 1}
                  hasPrev={index > 0}
                />
              ))}
            </>
          )}
        </Styled.ItemsContainer>
      </Styled.Container>
    </ScrollableElementProvider>
  )
}

export default memo(ImagesTab)
